<template>
  <layout>
    <div class="LegalPage">
      <div class="Layout">
        <div class="Layout-section">
          <grid-item md="12" sm="4">
            <ai-breadcrumb
              :breadcrumb-items="breadcrumbs"
              class="LegalPage-breadcrumb"
              variant="positive" />
          </grid-item>
          <grid-item md="10" offset-md="1" sm="4">
            <ai-typo
              id="LegalPage-title"
              as="h1"
              class="LegalPage-title"
              variant="heading-02">
              {{ legalPage?.title }}
            </ai-typo>
            <div class="LegalPage-description">
              <ai-rich-text :document="legalPage?.description" />
            </div>
          </grid-item>
        </div>
      </div>

      <section class="LegalPage-expansionPanels">
        <div class="Layout">
          <div class="Layout-section">
            <ai-typo as="h2" class="sr-only">
              {{
                $t(
                  'ux.molecules.legalMentions.tAdnCLegalMention.tAndCListTitle',
                )
              }}
            </ai-typo>
            <grid-item
              aria-labelledby="LegalPage-title"
              md="10"
              offset-md="1"
              role="list"
              sm="4">
              <template v-for="panel in panels" :key="panel.title">
                <ai-expansion-panel
                  :controls-id="slugify(panel.title)"
                  :title="panel.title"
                  role="listitem">
                  <ai-rich-text :document="panel.description" />
                </ai-expansion-panel>
                <ai-separator :margin="2" />
              </template>
            </grid-item>
          </div>
        </div>
      </section>
    </div>
  </layout>
</template>

<script lang="ts" setup>
import axios from 'axios';
import type { RouteLocationRaw } from 'vue-router';

import { useLegalPageAsyncData } from '~/domains/graphql';
import { useHeaderConfiguration } from '~/domains/ux';
import { getRawBackendUrl, pageView, slugify } from '~/helpers';

const route = useRoute();

const { data, error } = await useLegalPageAsyncData({
  slug: route.params.slug as string,
});
useAsyncDataError(error, 'legal-page-slug');

const { t } = useI18n();

useSeoMetadata({
  metadata: data.value?.legalPage?.seo,
  fallback: {
    title: t('seo.legalPage.title', { name: data.value?.legalPage?.title }),
    description: data.value?.legalPage?.description,
  },
});

definePageMeta({
  async validate(routeToValidate) {
    const slug = routeToValidate.params.slug;
    const isPreview = routeToValidate.query.draft === 'true';
    const language = routeToValidate.fullPath.slice(1, 3);
    const serverUri = getRawBackendUrl();

    try {
      const response = await axios.get(
        `${serverUri}/content/legal-page/by-slug`,
        {
          headers: {
            'x-preview': isPreview,
          },
          params: { slug, language },
        },
      );

      return response.data;
    } catch (err) {
      return false;
    }
  },
});

const legalPage = computed(() => data.value?.legalPage);

const localePath = useLocalePath();

const breadcrumbs = computed(() => [
  { label: t('navigation.page.home'), route: localePath({ name: 'index' }) },
  {
    label: legalPage.value?.title as string,
    route: localePath(route),
    currentPage: true,
  },
]);

// Locale switcher override
const routeLocalization = computed(() => {
  const payload = {} as Record<string, RouteLocationRaw>;

  for (const [language, path] of Object.entries(
    legalPage?.value?.slugByLanguage || {},
  )) {
    payload[language] = {
      name: 'legal-page-slug',
      params: { slug: path as string },
    };
  }

  return payload;
});

useHeaderConfiguration({
  routeLocalization: routeLocalization.value,
});

const panels = computed(() =>
  legalPage?.value?.sections?.map(section => ({
    title: section?.title ?? '',
    description: section?.description ?? '',
  })),
);

onMounted(() => {
  pageView({
    page_category: 'legal (t&c, privacy policy)',
    page_name: 'legal::terms and conditions',
    page_sub_category_level_1: 'legal',
    page_sub_category_level_2: 'terms and conditions',
  });
});
</script>

<style lang="scss" scoped>
@use '@/assets/styles/utilities/colors';
@use '@/assets/styles/utilities/constants';
@use '@/assets/styles/utilities/mq';

.LegalPage {
  margin-top: constants.$header;
}

.LegalPage-breadcrumb {
  display: none;

  @media (mq.$from-medium) {
    display: flex;
    margin-bottom: constants.$margin-03;
  }
}

.LegalPage-title {
  margin-bottom: constants.$margin-03;
  text-align: center;
}

.LegalPage-description {
  margin-bottom: constants.$margin-03;

  @media (mq.$from-medium) {
    margin-bottom: constants.$margin-05;
  }
}

.LegalPage-expansionPanels {
  padding-top: constants.$padding-05;
  padding-bottom: constants.$padding-05;
  background-color: colors.$gold-100;

  @media (mq.$from-medium) {
    padding-top: constants.$padding-07;
    padding-bottom: constants.$padding-07;
  }
}

.LegalPage-description p {
  margin-bottom: constants.$margin-02;
}

.LegalPage-expansionPanels p {
  margin-bottom: constants.$margin-02;
}

.LegalPage ol {
  list-style: decimal;
  margin-left: 1em;

  ol {
    list-style: lower-alpha;
    margin-left: 1em;

    ol {
      list-style: lower-roman;
      margin-left: 1em;
    }
  }
}

.LegalPage ul {
  list-style: disc;
  margin-left: 1em;

  ul {
    list-style: circle;
    margin-left: 1em;

    ul {
      list-style: square;
      margin-left: 1em;
    }
  }
}

.LegalPage blockquote {
  margin-block-start: 0;
  margin-block-end: 0;
  margin-inline-start: 0;
  margin-inline-end: 0;
  padding-left: constants.$padding-02;
  padding-right: constants.$padding-02;
  border-left: 2px solid currentColor;
  font-style: italic;
}
</style>
